import { useEditor, EditorContent, Editor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Color } from '@tiptap/extension-color';
import ListItem from '@tiptap/extension-list-item';
import TextStyle from '@tiptap/extension-text-style';
import styled from 'styled-components';
import { memo, useEffect } from 'react';

type Props = {
  content: string;
  setContent: (content: string) => void;
};

const EditorContainerStyled = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  /* Toolbar styling */
  .control-group {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 8px;
    background-color: #f5f5f5;
    border: 1px solid ${({ theme }) => theme.border};
    border-bottom: none;
    border-radius: 7px 7px 0 0;
    user-select: none;

    .button-group {
      display: flex;
      gap: 0.3vw;
      border-right: 1px solid #ddd;
      padding-right: 8px;
      margin-right: 8px;

      &:last-child {
        border-right: none;
      }

      button {
        padding: 6px 8px;
        border-radius: 4px;
        box-shadow: none;
        border: none;
        outline: none;
        color: ${({ theme }) => theme.primaryGray};
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
          background-color: #e0e0e0;
        }

        &.is-active {
          background-color: ${({ theme }) => theme.secondaryGray};
          color: white;
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }
  }

  /* Icon styling */
  .icon {
    font-family: 'Material Symbols Outlined';
    font-size: 18px;
    font-weight: normal;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Editor container */
  .editorContainer {
    border-radius: 0 0 7px 7px;
    border: 1px solid ${({ theme }) => theme.border};
    color: ${({ theme }) => theme.primaryGray};
    background-color: ${({ theme }) => theme.background};
    border-radius: 5px;
    resize: none;

    .tiptap {
      padding: 18px 24px;
      min-height: 25vh;
      outline: none;
      font-family: 'fontRegular', sans-serif;
      font-size: 14px;
      /* line-height: 1.5; */
      color: #333;

      /* Headings with desktop-like styling */
      h1 {
        font-size: 28px;
        font-weight: 700;
        margin: 6px 0 2px;
        color: #222;
        text-align: left;
      }

      h2 {
        font-size: 24px;
        font-weight: 600;
        margin: 6px 0 2px;
        color: #333;
      }

      h3 {
        font-size: 20px;
        font-weight: 600;
        margin: 6px 0 2px;
        color: #444;
      }

      h4 {
        font-size: 18px;
        font-weight: 600;
        margin: 6px 0 2px;
        color: #555;
      }

      h5 {
        font-size: 16px;
        font-weight: 600;
        margin: 6px 0 2px;
        color: #666;
      }

      h6 {
        font-size: 14px;
        font-weight: 600;
        margin: 6px 0 2px;
        color: #777;
      }

      /* First heading needs less top margin */
      h1:first-child,
      h2:first-child,
      h3:first-child,
      h4:first-child,
      h5:first-child,
      h6:first-child {
        margin-top: 0;
      }

      /* Bold text */
      strong {
        font-weight: 700;
      }

      /* Bullet list styling */
      ul {
        padding-left: 24px;

        li {
          margin-bottom: 8px;
          position: relative;

          &::marker {
            color: #555;
          }

          /* Nested lists */
          ul {
            margin: 8px 0;
          }
        }
      }

      /* Ordered list styling */
      ol {
        padding-left: 24px;

        li {
          margin-bottom: 8px;

          &::marker {
            color: #555;
            font-weight: 600;
          }

          /* Nested lists */
          ol {
            margin: 8px 0;
          }
        }
      }

      /* Paragraph styling */
      p {
        &:first-child {
          margin-top: 0;
        }
      }

      /* Blockquote styling */
      blockquote {
        border-left: 3px solid #ddd;
        margin: 16px 0;
        padding-left: 16px;
        color: #666;
        font-style: italic;
      }

      /* Inline code styling */
      code {
        background-color: #f0f0f0;
        border-radius: 3px;
        padding: 1px 4px;
        font-family: monospace;
        font-size: 0.9em;
        color: #c7254e;
      }

      /* Code block styling */
      pre {
        background-color: #f5f5f5;
        border-radius: 4px;
        padding: 12px;
        margin: 16px 0;
        overflow: auto;

        code {
          background: none;
          padding: 0;
          color: #333;
          font-size: 1em;
        }
      }
    }
  }

  /* Save button styling */
  button {
    padding: 8px 16px;
    border-radius: 4px;
    border: none;
    background-color: ${({ theme }) => theme.border};
    color: white;
    cursor: pointer;
    font-weight: 500;
    transition: background-color 0.2s;
    align-self: flex-end;
    margin-top: 12px;

    &:hover {
      background-color: ${({ theme }) => theme.brand || '#2a5885'};
    }
  }
`;

const MenuBar = memo(({ editor }: { editor: Editor }) => {
  return (
    <div className="control-group">
      <div className="button-group">
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().undo().run();
          }}
          disabled={!editor.can().undo()}
          title="Undo"
        >
          <span className="icon">undo</span>
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().redo().run();
          }}
          disabled={!editor.can().redo()}
          title="Redo"
        >
          <span className="icon">redo</span>
        </button>
      </div>
      <div className="button-group">
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().toggleBold().run();
          }}
          className={editor.isActive('bold') ? 'is-active' : ''}
          disabled={!editor.can().toggleBold()}
          title="Bold"
        >
          <span className="icon">format_bold</span>
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().toggleItalic().run();
          }}
          className={editor.isActive('italic') ? 'is-active' : ''}
          disabled={!editor.can().toggleItalic()}
          title="Italic"
        >
          <span className="icon">format_italic</span>
        </button>

        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().toggleStrike().run();
          }}
          disabled={!editor.can().toggleStrike()}
          className={editor.isActive('strike') ? 'is-active' : ''}
          title="Strikethrough"
        >
          <span className="icon">format_strikethrough</span>
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().toggleCode().run();
          }}
          disabled={!editor.can().toggleCode()}
          className={editor.isActive('code') ? 'is-active' : ''}
          title="Inline Code"
        >
          <span className="icon">code</span>
        </button>
      </div>
      <div className="button-group">
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().setParagraph().run();
          }}
          className={editor.isActive('paragraph') ? 'is-active' : ''}
          title="Normal text"
        >
          <span className="icon">format_paragraph</span>
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().toggleBulletList().run();
          }}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          disabled={!editor.can().toggleBulletList()}
          title="Bullet List"
        >
          <span className="icon">format_list_bulleted</span>
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().toggleOrderedList().run();
          }}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          disabled={!editor.can().toggleOrderedList()}
          title="Ordered List"
        >
          <span className="icon">format_list_numbered</span>
        </button>

        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().toggleHeading({ level: 1 }).run();
          }}
          className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
          disabled={!editor.can().toggleHeading({ level: 1 })}
          title="Heading 1"
        >
          <span className="icon">format_h1</span>
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().toggleHeading({ level: 2 }).run();
          }}
          className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
          disabled={!editor.can().toggleHeading({ level: 2 })}
          title="Heading 2"
        >
          <span className="icon">format_h2</span>
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().toggleHeading({ level: 3 }).run();
          }}
          className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
          disabled={!editor.can().toggleHeading({ level: 3 })}
          title="Heading 3"
        >
          <span className="icon">format_h3</span>
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().toggleHeading({ level: 4 }).run();
          }}
          className={editor.isActive('heading', { level: 4 }) ? 'is-active' : ''}
          disabled={!editor.can().toggleHeading({ level: 4 })}
          title="Heading 4"
        >
          <span className="icon">format_h4</span>
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().toggleHeading({ level: 5 }).run();
          }}
          className={editor.isActive('heading', { level: 5 }) ? 'is-active' : ''}
          disabled={!editor.can().toggleHeading({ level: 5 })}
          title="Heading 5"
        >
          <span className="icon">format_h5</span>
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().toggleHeading({ level: 6 }).run();
          }}
          className={editor.isActive('heading', { level: 6 }) ? 'is-active' : ''}
          disabled={!editor.can().toggleHeading({ level: 6 })}
          title="Heading 6"
        >
          <span className="icon">format_h6</span>
        </button>
      </div>
    </div>
  );
});

const CustomEditor = (props: Props) => {
  const { content, setContent } = props;

  const extensions = [
    Color.configure({ types: [TextStyle.name, ListItem.name] }),
    TextStyle,
    StarterKit.configure({
      bulletList: {
        keepMarks: true,
        keepAttributes: false,
      },
      orderedList: {
        keepMarks: true,
        keepAttributes: false,
      },
    }),
  ];
  const editor = useEditor({
    extensions,
    content: content,
    autofocus: true,
    onFocus: () => {
      console.log('Editor focused');
    },
    onUpdate: ({ editor }) => {
      setContent(editor.getHTML());
    },
  });

  // Sync editor when content prop changes externally (e.g. async data load)
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content, false);
    }
  }, [content, editor]);

  if (!editor) {
    return <div>Loading...</div>;
  }
  return (
    <EditorContainerStyled>
      <MenuBar editor={editor} />
      <EditorContent className="editorContainer" editor={editor}></EditorContent>
    </EditorContainerStyled>
  );
};

export default CustomEditor;
